import { useCallback, useEffect, useState } from 'react';

/**
 * Materiality filter state for the Deals card field grid.
 *
 * Three modes, persisted to localStorage:
 *   - 'all'      — show every proposed field regardless of materiality
 *   - 'changes'  — show only fields where decision != 'no_change'
 *   - 'material' — show only fields with materiality == 'material'
 *                  (still subject to the same "must be a change" filter)
 *
 * Spec: docs/archive/pre-cut-2026-08/phase-b-deals-card-fields-spec.md §6.3.
 *
 * Default depends on card kind (passed by caller):
 *   - NEW_DEAL    → 'all' (every field is proposed; nothing to hide)
 *   - FIELD_UPDATE → 'changes' (operator sees only what shifted)
 */

export type MaterialityFilter = 'all' | 'changes' | 'material';

const LOCALSTORAGE_KEY = 'dealsPage.materialityFilter';

function _readPersisted(): MaterialityFilter | null {
  try {
    const v = window.localStorage.getItem(LOCALSTORAGE_KEY);
    if (v === 'all' || v === 'changes' || v === 'material') return v;
  } catch {
    /* SSR or storage unavailable — silently ignore. */
  }
  return null;
}

function _writePersisted(value: MaterialityFilter): void {
  try {
    window.localStorage.setItem(LOCALSTORAGE_KEY, value);
  } catch {
    /* ignore — persistence is best-effort */
  }
}

export function useMaterialityFilter(
  defaultMode: MaterialityFilter,
): [MaterialityFilter, (next: MaterialityFilter) => void] {
  const [mode, setModeState] = useState<MaterialityFilter>(() => {
    // Persisted value wins over the default-by-card-kind so user
    // preference survives card-kind transitions in the same session.
    return _readPersisted() ?? defaultMode;
  });

  // If the persisted value changes outside this hook (multi-tab edits),
  // pick it up on next render. Cheap because storage events fire only
  // on writes from OTHER tabs.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== LOCALSTORAGE_KEY) return;
      const v = e.newValue;
      if (v === 'all' || v === 'changes' || v === 'material') {
        setModeState(v);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setMode = useCallback((next: MaterialityFilter) => {
    setModeState(next);
    _writePersisted(next);
  }, []);

  return [mode, setMode];
}
